import React, { useState, useRef, useEffect } from 'react';

/**
 * OptimizedImage - Composant d'image optimisé pour de meilleures performances
 * 
 * Fonctionnalités :
 * - Lazy loading natif
 * - Placeholder avec effet blur pendant le chargement
 * - Décoding asynchrone pour ne pas bloquer le rendu
 * - Gestion des erreurs avec image de fallback
 * - Support des classes CSS personnalisées
 */
export const OptimizedImage = ({
  src,
  alt,
  className = '',
  fallbackSrc = 'https://images.unsplash.com/photo-1605202880971-478c287c1ebc?w=400',
  width,
  height,
  objectFit = 'cover',
  priority = false,
  onLoad,
  onError,
  ...props
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isInView, setIsInView] = useState(priority);
  const imgRef = useRef(null);

  // Intersection Observer pour lazy loading amélioré
  useEffect(() => {
    if (priority) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: '100px', // Précharge 100px avant d'entrer dans le viewport
        threshold: 0.01
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, [priority]);

  const handleLoad = (e) => {
    setIsLoaded(true);
    onLoad?.(e);
  };

  const handleError = (e) => {
    setHasError(true);
    onError?.(e);
  };

  const imageSrc = hasError ? fallbackSrc : src;

  return (
    <div 
      ref={imgRef}
      className={`relative overflow-hidden ${className}`}
      style={{ 
        width: width || '100%', 
        height: height || '100%',
      }}
    >
      {/* Placeholder avec effet blur */}
      {!isLoaded && (
        <div 
          className="absolute inset-0 bg-gradient-to-br from-zinc-200 to-zinc-300 animate-pulse"
          style={{ 
            backdropFilter: 'blur(10px)',
          }}
        >
          <div className="absolute inset-0 flex items-center justify-center">
            <svg 
              className="w-10 h-10 text-zinc-400 animate-pulse" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={1.5} 
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" 
              />
            </svg>
          </div>
        </div>
      )}
      
      {/* Image principale */}
      {isInView && (
        <img
          src={imageSrc}
          alt={alt}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          onLoad={handleLoad}
          onError={handleError}
          className={`transition-opacity duration-500 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
          style={{
            width: '100%',
            height: '100%',
            objectFit: objectFit,
          }}
          {...props}
        />
      )}
    </div>
  );
};

/**
 * OptimizedBackgroundImage - Pour les images en arrière-plan
 */
export const OptimizedBackgroundImage = ({
  src,
  alt = '',
  className = '',
  children,
  overlay = false,
  overlayColor = 'rgba(0,0,0,0.5)',
  priority = false,
  ...props
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(priority);
  const containerRef = useRef(null);

  useEffect(() => {
    if (priority) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '100px', threshold: 0.01 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [priority]);

  useEffect(() => {
    if (!isInView || !src) return;
    
    const img = new Image();
    img.src = src;
    img.onload = () => setIsLoaded(true);
  }, [isInView, src]);

  return (
    <div 
      ref={containerRef}
      className={`relative overflow-hidden ${className}`}
      {...props}
    >
      {/* Placeholder */}
      {!isLoaded && (
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-300 to-zinc-400 animate-pulse" />
      )}
      
      {/* Background Image */}
      {isInView && (
        <div
          className={`absolute inset-0 bg-cover bg-center transition-opacity duration-700 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
          style={{ backgroundImage: `url(${src})` }}
          role="img"
          aria-label={alt}
        />
      )}
      
      {/* Overlay */}
      {overlay && (
        <div 
          className="absolute inset-0" 
          style={{ backgroundColor: overlayColor }}
        />
      )}
      
      {/* Content */}
      <div className="relative z-10">
        {children}
      </div>
    </div>
  );
};

export default OptimizedImage;
